<template>
  <div class="node-graph-page">
    <div v-if="!game" style="text-align: center; padding: 40px; color: #909399;">请选择游戏</div>
    <template v-else>
      <GraphToolbar
        :list-view-route="nodeDetailRoute"
        @add-node="openCreateDialog"
        @auto-layout="autoLayout"
        @zoom-in="zoomIn"
        @zoom-out="zoomOut"
        @zoom-fit="zoomFit"
        @reset-view="resetView"
        @export-svg="exportSVG"
      />
      <div class="graph-body">
        <!-- 章节导航：点击章节定位到该章节第一个节点 -->
        <div class="chapter-nav" :class="{ collapsed: chapterNavCollapsed }" v-if="chapters.length">
          <div class="chapter-nav-header">
            <span v-show="!chapterNavCollapsed" class="chapter-nav-title">
              <i class="el-icon-notebook-2"></i> 章节导航
            </span>
            <i
              class="chapter-nav-toggle"
              :class="chapterNavCollapsed ? 'el-icon-d-arrow-right' : 'el-icon-d-arrow-left'"
              :title="chapterNavCollapsed ? '展开章节导航' : '收起章节导航'"
              @click="chapterNavCollapsed = !chapterNavCollapsed"
            ></i>
          </div>
          <ul v-show="!chapterNavCollapsed" class="chapter-nav-list">
            <li
              v-for="(ch, idx) in chapters"
              :key="ch.id"
              class="chapter-nav-item"
              :class="{ active: activeChapterId === ch.id, empty: chapterNodeCount(ch.id) === 0 }"
              :title="ch.title"
              @click="focusChapter(ch.id)"
            >
              <span class="cni-index">{{ idx + 1 }}</span>
              <span class="cni-title">{{ ch.title || '未命名章节' }}</span>
              <span class="cni-count">{{ chapterNodeCount(ch.id) }}</span>
            </li>
          </ul>
        </div>

        <GraphCanvas
          ref="graphCanvas"
          :nodes="nodes"
          :positions="graphData.positions"
          :connections="graphData.connections"
          :root-node-ids="rootNodeIds"
          @node-dblclick="openEditDialog"
          @node-click="onNodeClick"
          @blank-click="onBlankClick"
          @node-moved="onNodeMoved"
          @node-moving="onNodeMoving"
          @edge-created="onEdgeCreated"
          @edge-contextmenu="onEdgeContextMenu"
          @node-contextmenu="onNodeContextMenu"
        />
      </div>
      <NodeEditDialog
        :visible.sync="editDialogVisible"
        :node="editingNode"
        :all-nodes="nodes"
        :roles="roles"
        :chapters="chapters"
        :game-id="game.id"
        :initial-connections="editingNodeConnections"
        :attribute-options="attributeNameList"
        :game-attributes="gameAttributes"
        @save="onNodeSaved"
        @delete="onNodeDeleted"
      />

      <!-- 右键菜单 -->
      <div
        v-show="contextMenu.visible"
        class="context-menu"
        :style="{ left: contextMenu.x + 'px', top: contextMenu.y + 'px' }"
      >
        <div class="context-menu-item danger" @click="handleContextMenuAction">
          <i class="el-icon-delete"></i> 删除
        </div>
      </div>

      <!-- 节点详情弹层：单击节点显示，点击空白消失 -->
      <div
        v-if="detail.visible && detail.node"
        class="node-detail-pop"
        :style="{ left: detail.x + 'px', top: detail.y + 'px' }"
        @click.stop
      >
        <div class="ndp-header" :class="detail.node.node_type === 'ai' ? 'is-ai' : 'is-fixed'">
          <span class="ndp-badge">{{ detail.node.node_type === 'ai' ? 'AI' : '固定' }}</span>
          <span class="ndp-title">{{ detail.node.title || '未命名节点' }}</span>
          <span class="ndp-id">#{{ detail.node.id }}</span>
          <span v-if="detail.node.is_be" class="ndp-be">💀 BE</span>
        </div>
        <div class="ndp-body">
          <div class="ndp-row">
            <span class="ndp-label">章节</span>
            <span class="ndp-val">{{ chapterTitleOf(detail.node) }}</span>
          </div>
          <div class="ndp-row" v-if="condsOf(detail.node).length">
            <span class="ndp-label">进入条件</span>
            <span class="ndp-val">🔒 {{ condsOf(detail.node).join('、') }}</span>
          </div>
          <div class="ndp-section">跳转关系</div>
          <div v-if="endingsOf(detail.node).length === 0" class="ndp-empty">无后续跳转</div>
          <div v-for="(jp, i) in endingsOf(detail.node)" :key="i" class="ndp-jump">
            <span class="ndp-ename">{{ jp.name }}</span>
            <span class="ndp-arrow">→</span>
            <span v-if="jp.target" class="ndp-target">{{ nodeTitleOf(jp.target) }} <em>#{{ jp.target }}</em></span>
            <span v-else class="ndp-target none">未连接</span>
          </div>
        </div>
        <div class="ndp-tip">双击节点可编辑</div>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Game } from '@/api/game'
import {
  getNodes,
  getChapters,
  updateNode,
  deleteNode,
  createNode,
  batchSavePositions,
  GameNode,
  Chapter,
  ProcessConfigV2,
  PlotEnding,
  EntryCondition,
} from '@/api/node'
import { getRoles, Role } from '@/api/role'
import { getGamePoints, GamePoint } from '@/api/gamePoint'
import { GameAttribute, getGameAttributes } from '@/api/gameAttribute'
import {
  loadGraphData,
  GraphLocalData,
} from '@/utils/graphStorage'
import GraphCanvas from '@/components/node-graph/GraphCanvas.vue'
import GraphToolbar from '@/components/node-graph/GraphToolbar.vue'
import NodeEditDialog from '@/components/node-graph/NodeEditDialog.vue'

@Component({
  components: { GraphCanvas, GraphToolbar, NodeEditDialog },
})
export default class NodeGraph extends Vue {
  @Prop({ required: true }) readonly game!: Game

  nodes: GameNode[] = []
  roles: Role[] = []
  chapters: Chapter[] = []
  gamePoints: GamePoint[] = []
  gameAttributes: GameAttribute[] = []
  graphData: GraphLocalData = { positions: {}, connections: {} }

  editDialogVisible = false
  editingNode: GameNode | null = null
  editingNodeConnections: Record<number, number> = {}

  // 章节导航
  chapterNavCollapsed = false
  activeChapterId: number | null = null

  contextMenu = {
    visible: false,
    x: 0,
    y: 0,
    type: '' as 'edge' | 'node',
    edgeData: null as any,
    nodeId: 0,
  }

  // 节点详情弹层
  detail: { visible: boolean; x: number; y: number; node: GameNode | null } = {
    visible: false,
    x: 0,
    y: 0,
    node: null,
  }

  get nodeDetailRoute() {
    if (this.game?.nodeVersion === 'v2.0') {
      return '/nodeDetailV2'
    }
    return '/nodeDetail'
  }

  get attributeNameList(): string[] {
    if (this.gameAttributes.length === 0) return ['智慧值', '灵敏值', '精神值', '圣母值', '黑化值']
    return [...new Set(this.gameAttributes.map(a => a.name))]
  }

  /** 识别根节点：不被任何连线指向的节点 */
  get rootNodeIds(): Set<number> {
    const childIds = new Set<number>()
    for (const conns of Object.values(this.graphData.connections)) {
      for (const targetId of Object.values(conns)) {
        childIds.add(targetId)
      }
    }
    const roots = new Set<number>()
    for (const node of this.nodes) {
      if (node.id && !childIds.has(node.id)) roots.add(node.id)
    }
    return roots
  }

  @Watch('game', { immediate: true })
  onGameChange(newGame: Game) {
    if (newGame && newGame.id) {
      this.loadData()
    }
  }

  mounted() {
    document.addEventListener('click', this.hideContextMenu)
  }

  beforeDestroy() {
    document.removeEventListener('click', this.hideContextMenu)
  }

  async loadData() {
    const gameId = this.game.id

    try {
      // 并行加载数据
      const [rolesRes, nodesRes, chaptersRes, gamePointsRes] = await Promise.all([
        getRoles(gameId),
        getNodes(gameId),
        getChapters(gameId),
        getGamePoints(gameId).catch(() => ({ data: [] })),
      ])

      this.roles = rolesRes
      this.chapters = chaptersRes.data || []
      this.gamePoints = (gamePointsRes as any).data || []

      // Load game attributes (non-blocking)
      getGameAttributes(gameId).then(attrs => { this.gameAttributes = attrs }).catch(() => { /* use defaults */ })

      // 处理节点数据
      const nodes: GameNode[] = []
      if (nodesRes.data && Array.isArray(nodesRes.data)) {
        for (const n of nodesRes.data) {
          // 初始化字段
          if (n.node_type === 'ai') {
            if (!n.user_special) n.user_special = []
            if (!n.story_background) n.story_background = ''
            if (n.hidden_ui === undefined) n.hidden_ui = false
            if (!n.current_plot_new) {
              n.current_plot_new = { plot_start: '', plot_process: '', plot_end: '' }
            }
            if (!n.process) n.process = []
            if (!n.egg) n.egg = []
            if (!n.user_messages_new) n.user_messages_new = []
            if (!n.output_examples) n.output_examples = []
          }
          if (!n.entry_conditions) n.entry_conditions = []
          // 数据兼容性处理
          if (n.entry_conditions && n.entry_conditions.length > 0) {
            n.entry_conditions = this.migrateEntryConditionTypes(n.entry_conditions)
          }
          nodes.push(n as GameNode)
        }
      }
      this.nodes = nodes

      // 从后端数据提取位置和连线
      this.graphData = this.extractGraphData(this.nodes)

      // 如果后端无有效位置数据，尝试从 localStorage 迁移
      const hasBackendPositions = this.nodes.some((n) => n.graph_x != null && n.graph_y != null && (n.graph_x !== 0 || n.graph_y !== 0))
      if (!hasBackendPositions && this.nodes.length > 0) {
        const localData = loadGraphData(gameId)
        if (Object.keys(localData.positions).length > 0) {
          await this.migrateFromLocalStorage(localData)
        } else {
          // 无任何位置数据，执行首次自动布局
          this.$nextTick(() => {
            this.compactRootPositions()
            this.applyTreeLayout()
            this.focusFirstNode()
          })
          return
        }
      }

      // 紧凑排列根节点 + 计算子节点位置
      this.compactRootPositions()
      this.applyTreeLayout()
      this.focusFirstNode()
    } catch (error) {
      console.error('加载数据失败:', error)
      this.$message.error('加载数据失败')
    }
  }

  /** 从节点数据中提取图谱位置和连线关系 */
  extractGraphData(nodes: GameNode[]): GraphLocalData {
    const positions: Record<number, { x: number; y: number }> = {}
    const connections: Record<number, Record<number, number>> = {}

    for (const node of nodes) {
      if (!node.id) continue

      // 提取位置（排除 0,0 —— 视为未定位）
      if (node.graph_x != null && node.graph_y != null && (node.graph_x !== 0 || node.graph_y !== 0)) {
        positions[node.id] = { x: node.graph_x, y: node.graph_y }
      }

      // 提取连线：AI 节点从 plot_end 的 next_node_id 提取
      if (node.node_type === 'ai' && node.current_plot_new) {
        const pe = node.current_plot_new.plot_end
        if (Array.isArray(pe)) {
          (pe as PlotEnding[]).forEach((ending, idx) => {
            if (ending.next_node_id != null) {
              if (!connections[node.id!]) connections[node.id!] = {}
              connections[node.id!][idx] = ending.next_node_id
            }
          })
        }
      } else if (node.node_type === 'fixed' && node.next_node_id != null) {
        connections[node.id] = { 0: node.next_node_id }
      }
    }

    return { positions, connections }
  }

  /** 从 localStorage 迁移数据到后端 */
  async migrateFromLocalStorage(localData: GraphLocalData) {
    try {
      // 迁移位置
      const positionsToSave = Object.entries(localData.positions).map(([nodeIdStr, pos]) => ({
        node_id: Number(nodeIdStr),
        graph_x: pos.x,
        graph_y: pos.y,
      }))
      if (positionsToSave.length > 0) {
        await batchSavePositions(positionsToSave)
      }

      // 迁移连线
      for (const [srcIdStr, conns] of Object.entries(localData.connections)) {
        const srcId = Number(srcIdStr)
        const node = this.nodes.find((n) => n.id === srcId)
        if (!node) continue

        if (node.node_type === 'ai' && node.current_plot_new && Array.isArray(node.current_plot_new.plot_end)) {
          const endings = node.current_plot_new.plot_end as PlotEnding[]
          let changed = false
          for (const [pIdxStr, targetId] of Object.entries(conns)) {
            const pIdx = Number(pIdxStr)
            if (endings[pIdx]) {
              endings[pIdx].next_node_id = targetId
              changed = true
            }
          }
          if (changed) {
            await updateNode(srcId, { current_plot_new: node.current_plot_new } as any)
          }
        } else if (node.node_type === 'fixed') {
          const targetId = conns[0]
          if (targetId != null) {
            await updateNode(srcId, { next_node_id: targetId } as any)
          }
        }
      }

      // 迁移成功，清除 localStorage
      const storageKey = `node_graph_${this.game.id}`
      localStorage.removeItem(storageKey)
      this.$message.success('图谱数据已从本地迁移到服务器')

      // 重新加载以获取最新数据
      await this.reloadNodes()
      this.graphData = this.extractGraphData(this.nodes)
    } catch (error) {
      console.error('迁移 localStorage 数据失败:', error)
      // 保留 localStorage，下次重试
      // 使用 localStorage 数据作为临时显示
      this.graphData = localData
    }
  }

  migrateEntryConditionTypes(conditions: EntryCondition[]): EntryCondition[] {
    const typeMap: Record<string, string> = {
      currency: '货币',
      attribute: '属性',
      affection: '好感度',
      item: '道具',
      costume: '服装',
    }
    return conditions.map((cond) => {
      if (typeMap[cond.type]) {
        return { ...cond, type: typeMap[cond.type] }
      }
      return cond
    })
  }

  filterEntryConditions(conditions: EntryCondition[]): any[] {
    return conditions.map((cond) => {
      const base = { type: cond.type }
      switch (cond.type) {
        case '货币':
          return { ...base, value: cond.value, is_free: cond.is_free }
        case '属性':
          // role_id：属性绑角色，缺省（存量数据）后端回退主控
          return { ...base, role_id: cond.role_id, attribute_name: cond.attribute_name, attribute_value: cond.attribute_value }
        case '好感度':
          return { ...base, role_id: cond.role_id, affection_value: cond.affection_value }
        case '道具':
          return { ...base, item_name: cond.item_name }
        case '服装':
          return { ...base, role_id: cond.costume_role_id, costume_role_id: cond.costume_role_id, costume_name: cond.costume_name }
        default:
          return base
      }
    })
  }

  // ========== 对话框 ==========
  openCreateDialog() {
    this.editingNode = null
    this.editingNodeConnections = {}
    this.editDialogVisible = true
  }

  openEditDialog(nodeId: number) {
    const node = this.nodes.find((n) => n.id === nodeId)
    if (!node) return
    this.detail.visible = false
    this.editingNode = node
    // 加载该节点的连线映射
    this.editingNodeConnections = this.graphData.connections[nodeId]
      ? { ...this.graphData.connections[nodeId] }
      : {}
    this.editDialogVisible = true
  }

  async onNodeSaved(nodeData: GameNode, processTargets: Record<number, number | undefined>) {
    try {
      const data = JSON.parse(JSON.stringify(nodeData))
      delete data.expanded

      // 固定节点对话处理
      if (data.node_type === 'fixed' && data.dialogue_text !== undefined) {
        data.dialogues = this.textToDialogues(data.dialogue_text)
        delete data.dialogue_text
      }

      // 过滤进入条件
      if (data.entry_conditions) {
        data.entry_conditions = this.filterEntryConditions(data.entry_conditions)
      }

      // 将连线目标写入节点数据：AI 节点写入 endings 的 next_node_id
      if (data.node_type === 'ai' && data.current_plot_new && Array.isArray(data.current_plot_new.plot_end)) {
        const endings = data.current_plot_new.plot_end as PlotEnding[]
        endings.forEach((ending: PlotEnding, idx: number) => {
          ending.next_node_id = processTargets[idx] ?? null
        })
      } else if (data.node_type === 'fixed') {
        data.next_node_id = processTargets[0] ?? null
      }

      if (data.id) {
        // 编辑
        await updateNode(data.id, data)
        this.$message.success('保存成功')
      } else {
        // 新建
        data.game_id = this.game.id
        data.version = 'v2.0'
        await createNode(data)
        this.$message.success('新增成功')
      }

      // 重新加载节点和图数据
      await this.reloadNodes()
      this.graphData = this.extractGraphData(this.nodes)
      this.compactRootPositions()
      this.applyTreeLayout()
    } catch (error: any) {
      console.error('保存节点失败:', error)
      this.$message.error('保存失败: ' + (error.response?.data?.message || error.message))
    }
  }

  async onNodeDeleted(nodeId: number) {
    try {
      await deleteNode(nodeId)
      this.$message.success('删除成功')
      await this.reloadNodes()
      this.graphData = this.extractGraphData(this.nodes)
      this.compactRootPositions()
      this.applyTreeLayout()
    } catch (error: any) {
      console.error('删除节点失败:', error)
      this.$message.error('删除失败: ' + (error.response?.data?.message || error.message))
    }
  }

  async reloadNodes() {
    try {
      const res = await getNodes(this.game.id)
      const nodes: GameNode[] = []
      if (res.data && Array.isArray(res.data)) {
        for (const n of res.data) {
          if (n.node_type === 'ai') {
            if (!n.process) n.process = []
            if (!n.egg) n.egg = []
            if (!n.user_messages_new) n.user_messages_new = []
            if (!n.output_examples) n.output_examples = []
            if (!n.current_plot_new) {
              n.current_plot_new = { plot_start: '', plot_process: '', plot_end: '' }
            }
          }
          if (!n.entry_conditions) n.entry_conditions = []
          if (n.entry_conditions && n.entry_conditions.length > 0) {
            n.entry_conditions = this.migrateEntryConditionTypes(n.entry_conditions)
          }
          nodes.push(n as GameNode)
        }
      }
      this.nodes = nodes
    } catch (error) {
      console.error('重新加载节点失败:', error)
    }
  }

  // ========== 树形布局 ==========

  /** 树形布局常量（从上到下：GEN_GAP=相邻代的纵向间距，SIB_GAP=同层兄弟的横向间距）*/
  private readonly GEN_GAP = 240
  private readonly SIB_GAP = 340

  /** 构建布局树结构（父→子映射），子节点按进度指标排序 */
  private buildLayoutTree(): { childrenOf: Record<number, number[]> } {
    const connections = this.graphData.connections
    const childrenOf: Record<number, number[]> = {}
    const parentOf: Record<number, number> = {}
    const childMinPIdx: Record<string, number> = {}

    for (const node of this.nodes) {
      if (node.id) childrenOf[node.id] = []
    }

    for (const srcIdStr of Object.keys(connections)) {
      const srcId = Number(srcIdStr)
      const conns = connections[srcId]
      const pIdxKeys = Object.keys(conns).map(Number).sort((a, b) => a - b)
      for (const pIdx of pIdxKeys) {
        const targetId = conns[pIdx]
        if (targetId == null) continue
        const pairKey = `${srcId}-${targetId}`
        if (childMinPIdx[pairKey] === undefined || pIdx < childMinPIdx[pairKey]) {
          childMinPIdx[pairKey] = pIdx
        }
        if (parentOf[targetId] === undefined && childrenOf[targetId] !== undefined) {
          if (!childrenOf[srcId]) childrenOf[srcId] = []
          if (!childrenOf[srcId].includes(targetId)) {
            childrenOf[srcId].push(targetId)
          }
          parentOf[targetId] = srcId
        }
      }
    }

    for (const key of Object.keys(childrenOf)) {
      const parentId = Number(key)
      childrenOf[parentId].sort((a, b) => {
        const aIdx = childMinPIdx[`${parentId}-${a}`] ?? Infinity
        const bIdx = childMinPIdx[`${parentId}-${b}`] ?? Infinity
        return aIdx - bIdx
      })
    }

    return { childrenOf }
  }

  /**
   * 计算树形布局：DFS 叶序 + 自底向上居中
   * 返回全量 positions
   */
  computeTreeLayout(): Record<number, { x: number; y: number }> {
    const positions = this.graphData.positions
    const { childrenOf } = this.buildLayoutTree()

    const newPositions: Record<number, { x: number; y: number }> = { ...positions }
    let leafIndex = 0

    for (const rootId of this.rootNodeIds) {
      const visited = new Set<number>()
      const nodeRow: Record<number, number> = {}
      const nodeDepth: Record<number, number> = {}

      const dfs = (id: number, depth: number) => {
        if (visited.has(id)) return
        visited.add(id)
        nodeDepth[id] = depth

        const children = childrenOf[id] || []
        const validChildren = children.filter(c => !visited.has(c))

        if (validChildren.length === 0) {
          nodeRow[id] = leafIndex
          leafIndex++
        } else {
          for (const child of validChildren) {
            dfs(child, depth + 1)
          }
          const childRows = validChildren.filter(c => nodeRow[c] !== undefined).map(c => nodeRow[c])
          if (childRows.length > 0) {
            nodeRow[id] = childRows.reduce((a, b) => a + b, 0) / childRows.length
          } else {
            nodeRow[id] = leafIndex
            leafIndex++
          }
        }
      }

      dfs(rootId, 0)

      const rootStored = positions[rootId] || { x: 80, y: 80 }
      const rootCol = nodeRow[rootId] ?? 0
      const xOffset = rootStored.x - rootCol * this.SIB_GAP

      for (const idStr of Object.keys(nodeRow)) {
        const id = Number(idStr)
        const depth = nodeDepth[id] ?? 0
        const col = nodeRow[id]

        if (id === rootId) {
          newPositions[id] = { x: rootStored.x, y: rootStored.y }
        } else {
          // 从上到下：depth 决定纵向（越深越靠下），兄弟顺序决定横向
          newPositions[id] = {
            x: col * this.SIB_GAP + xOffset,
            y: rootStored.y + depth * this.GEN_GAP,
          }
        }
      }
    }

    return newPositions
  }

  /** BFS 获取某节点的所有后代 ID */
  getSubtreeNodeIds(rootId: number): Set<number> {
    const connections = this.graphData.connections
    // 构建 childrenOf
    const childrenOf: Record<number, Set<number>> = {}
    for (const srcIdStr of Object.keys(connections)) {
      const srcId = Number(srcIdStr)
      const conns = connections[srcId]
      for (const targetId of Object.values(conns)) {
        if (targetId == null) continue
        if (!childrenOf[srcId]) childrenOf[srcId] = new Set()
        childrenOf[srcId].add(targetId)
      }
    }

    const result = new Set<number>()
    const queue = [rootId]
    const visited = new Set<number>()
    visited.add(rootId)
    while (queue.length > 0) {
      const id = queue.shift()!
      const children = childrenOf[id]
      if (children) {
        for (const childId of children) {
          if (!visited.has(childId)) {
            visited.add(childId)
            result.add(childId)
            queue.push(childId)
          }
        }
      }
    }
    return result
  }

  /** 紧凑排列根节点：测量每棵树的实际尺寸，消除多余间距 */
  compactRootPositions() {
    const startX = 80
    const startY = 80
    const gap = 60
    const rootIds = [...this.rootNodeIds].sort((a, b) => a - b)
    if (rootIds.length === 0) return

    const { childrenOf } = this.buildLayoutTree()

    // Phase 1: 将所有根节点放在同一顶部基线（x=0），计算临时布局以测量每棵树的横向尺寸
    for (const rootId of rootIds) {
      this.graphData.positions[rootId] = { x: 0, y: startY }
    }
    const tempPositions = this.computeTreeLayout()

    // Phase 2: 测量每棵树的横向边界，从左到右紧凑排列（每棵树自身从上往下流）
    let currentLeft = startX
    for (const rootId of rootIds) {
      // BFS 找到该树的所有节点
      const treeNodes: number[] = [rootId]
      const visited = new Set([rootId])
      const queue = [rootId]
      while (queue.length > 0) {
        const id = queue.shift()!
        for (const child of (childrenOf[id] || [])) {
          if (!visited.has(child)) {
            visited.add(child)
            treeNodes.push(child)
            queue.push(child)
          }
        }
      }

      // 计算树的 X 边界（相对于根节点位置）
      const rootX = tempPositions[rootId]?.x ?? 0
      let minRelX = 0
      let maxRelX = 0
      for (const nid of treeNodes) {
        const p = tempPositions[nid]
        if (p) {
          const relX = p.x - rootX
          minRelX = Math.min(minRelX, relX)
          maxRelX = Math.max(maxRelX, relX)
        }
      }

      // 将根节点放在正确位置，使树的最左侧对齐 currentLeft
      this.graphData.positions[rootId] = { x: currentLeft - minRelX, y: startY }

      // 推进到下一棵树（加上节点卡片宽度估算 + 间距）
      currentLeft += (maxRelX - minRelX) + 280 + gap
    }
  }

  /** 应用树形布局并保存（仅保存变化的位置） */
  applyTreeLayout() {
    const oldPositions = this.graphData.positions
    const newPositions = this.computeTreeLayout()
    this.graphData = { ...this.graphData, positions: newPositions }

    // 只保存实际变化的位置
    const changedPositions: Array<{ node_id: number; graph_x: number; graph_y: number }> = []
    for (const [nodeIdStr, pos] of Object.entries(newPositions)) {
      const nodeId = Number(nodeIdStr)
      const old = oldPositions[nodeId]
      if (!old || Math.abs(old.x - pos.x) > 0.5 || Math.abs(old.y - pos.y) > 0.5) {
        changedPositions.push({ node_id: nodeId, graph_x: pos.x, graph_y: pos.y })
      }
    }
    if (changedPositions.length > 0) {
      batchSavePositions(changedPositions).catch((err: any) => {
        console.error('批量保存位置失败:', err)
      })
    }
  }

  // ========== 图交互 ==========
  onNodeMoved(nodeId: number, x: number, y: number) {
    // 仅根节点可触发移动
    if (!this.rootNodeIds.has(nodeId)) return

    // 更新根节点存储位置
    this.graphData.positions[nodeId] = { x, y }
    // 重新计算树布局并保存所有节点位置
    this.applyTreeLayout()
  }

  /** 拖拽过程中实时移动子树 */
  onNodeMoving(nodeId: number, x: number, y: number) {
    if (!this.rootNodeIds.has(nodeId)) return

    const oldPos = this.graphData.positions[nodeId]
    if (!oldPos) return

    const dx = x - oldPos.x
    const dy = y - oldPos.y
    if (dx === 0 && dy === 0) return

    // 更新根节点位置
    this.graphData.positions[nodeId] = { x, y }

    // 获取子树节点并通过 X6 API 直接移动
    const subtreeIds = this.getSubtreeNodeIds(nodeId)
    const canvas = this.$refs.graphCanvas as any
    const graph = canvas?.getGraph()
    if (!graph) return

    for (const childId of subtreeIds) {
      const childPos = this.graphData.positions[childId]
      if (childPos) {
        const newX = childPos.x + dx
        const newY = childPos.y + dy
        this.graphData.positions[childId] = { x: newX, y: newY }
        // 直接通过 X6 API 设置位置，不触发事件循环
        const cell = graph.getCellById(String(childId))
        if (cell && cell.isNode()) {
          cell.setPosition(newX, newY, { silent: true })
        }
      }
    }
  }

  async onEdgeCreated(sourceNodeId: number, processIndex: number, targetNodeId: number) {
    try {
      const node = this.nodes.find((n) => n.id === sourceNodeId)
      if (!node) return

      // BE 结局是终点，不能有后续节点
      if (node.is_be) {
        this.$message.warning(`「${node.title || '该节点'}」已标记为 BE 结局，不能添加后续节点，请先关闭 BE 开关`)
        await this.reloadNodes()
        return
      }

      if (node.node_type === 'ai' && node.current_plot_new) {
        const pe = node.current_plot_new.plot_end
        if (Array.isArray(pe) && (pe as PlotEnding[])[processIndex]) {
          (pe as PlotEnding[])[processIndex].next_node_id = targetNodeId
          await updateNode(sourceNodeId, { current_plot_new: node.current_plot_new } as any)
        }
      } else if (node.node_type === 'fixed') {
        await updateNode(sourceNodeId, { next_node_id: targetNodeId } as any)
        node.next_node_id = targetNodeId
      }

      // 更新本地连线数据
      if (!this.graphData.connections[sourceNodeId]) {
        this.graphData.connections[sourceNodeId] = {}
      }
      this.graphData.connections[sourceNodeId][processIndex] = targetNodeId
      this.graphData = { ...this.graphData }
      // 连线变化后重新紧凑排列 + 计算树形布局
      this.compactRootPositions()
      this.applyTreeLayout()
      this.$message.success('连线创建成功')
    } catch (error: any) {
      console.error('保存连线失败:', error)
      this.$message.error('保存连线失败')
    }
  }

  onEdgeContextMenu(edge: any, e: MouseEvent) {
    this.contextMenu = {
      visible: true,
      x: e.clientX,
      y: e.clientY,
      type: 'edge',
      edgeData: edge.getData?.() || edge.data,
      nodeId: 0,
    }
  }

  // ========== 节点详情弹层 ==========
  onNodeClick(nodeId: number, e: MouseEvent) {
    const node = this.nodes.find((n) => n.id === nodeId)
    if (!node) return
    const pad = 14
    const w = 320
    const h = 300
    let x = e.clientX + pad
    let y = e.clientY + pad
    if (x + w > window.innerWidth) x = window.innerWidth - w - pad
    if (y + h > window.innerHeight) y = Math.max(pad, window.innerHeight - h - pad)
    this.detail = { visible: true, x, y, node }
  }

  onBlankClick() {
    this.detail.visible = false
  }

  /** 章节标题 */
  chapterTitleOf(node: GameNode): string {
    const ch = this.chapters.find((c) => c.id === node.game_chapter_id)
    return ch ? ch.title : '—'
  }

  /** 目标节点标题 */
  nodeTitleOf(id: number): string {
    const n = this.nodes.find((nn) => nn.id === id)
    return n ? (n.title || '未命名节点') : '（跨章节）'
  }

  /** 进入条件类型列表 */
  condsOf(node: GameNode): string[] {
    return (node.entry_conditions || []).map((c) => c.type)
  }

  /** 跳转关系：AI 节点按结局，固定节点为"下一节点" */
  endingsOf(node: GameNode): Array<{ name: string; target: number | null }> {
    if (node.node_type === 'ai') {
      const pe = node.current_plot_new && node.current_plot_new.plot_end
      if (Array.isArray(pe)) {
        return (pe as PlotEnding[]).map((ending, idx) => ({
          name: ending.name || `结局${idx + 1}`,
          target: ending.next_node_id ?? null,
        }))
      }
      return []
    }
    return [{ name: '下一节点', target: node.next_node_id ?? null }]
  }

  onNodeContextMenu(nodeId: number, e: MouseEvent) {
    this.contextMenu = {
      visible: true,
      x: e.clientX,
      y: e.clientY,
      type: 'node',
      edgeData: null,
      nodeId,
    }
  }

  hideContextMenu() {
    this.contextMenu.visible = false
  }

  async handleContextMenuAction() {
    if (this.contextMenu.type === 'edge') {
      const data = this.contextMenu.edgeData
      if (data && data.sourceNodeId !== undefined && data.processIndex !== undefined) {
        try {
          const node = this.nodes.find((n) => n.id === data.sourceNodeId)
          if (node) {
            if (node.node_type === 'ai' && node.current_plot_new) {
              const pe = node.current_plot_new.plot_end
              if (Array.isArray(pe) && (pe as PlotEnding[])[data.processIndex]) {
                (pe as PlotEnding[])[data.processIndex].next_node_id = null
                await updateNode(data.sourceNodeId, { current_plot_new: node.current_plot_new } as any)
              }
            } else if (node.node_type === 'fixed') {
              await updateNode(data.sourceNodeId, { next_node_id: null } as any)
              node.next_node_id = null
            }
          }
          // 更新本地连线数据
          if (this.graphData.connections[data.sourceNodeId]) {
            delete this.graphData.connections[data.sourceNodeId][data.processIndex]
            if (Object.keys(this.graphData.connections[data.sourceNodeId]).length === 0) {
              delete this.graphData.connections[data.sourceNodeId]
            }
          }
          this.graphData = { ...this.graphData }
          // 连线变化后重新紧凑排列 + 计算树形布局
          this.compactRootPositions()
          this.applyTreeLayout()
          this.$message.success('连线已删除')
        } catch (error: any) {
          console.error('删除连线失败:', error)
          this.$message.error('删除连线失败')
        }
      }
    } else if (this.contextMenu.type === 'node') {
      this.$confirm('确定删除该节点吗？', '提示', {
        confirmButtonText: '删除',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(() => {
        this.onNodeDeleted(this.contextMenu.nodeId)
      }).catch(() => { /* cancelled */ })
    }
    this.hideContextMenu()
  }

  // ========== 缩放和布局 ==========
  zoomIn() {
    const canvas = this.$refs.graphCanvas as any
    canvas?.zoomIn()
  }

  zoomOut() {
    const canvas = this.$refs.graphCanvas as any
    canvas?.zoomOut()
  }

  zoomFit() {
    const canvas = this.$refs.graphCanvas as any
    canvas?.zoomFit()
  }

  resetView() {
    const canvas = this.$refs.graphCanvas as any
    canvas?.resetView()
  }

  exportSVG() {
    const canvas = this.$refs.graphCanvas as any
    if (!canvas) return
    const title = (this.game && this.game.title) ? this.game.title : '节点图谱'
    canvas.exportSVG(`${title}-节点图谱.svg`)
  }

  autoLayout() {
    if (this.nodes.length === 0) return

    this.compactRootPositions()
    this.applyTreeLayout()

    this.$nextTick(() => {
      const canvas = this.$refs.graphCanvas as any
      canvas?.zoomFit()
    })
  }

  /** 在一组节点里挑“最靠上”的：位置 y→x 优先，兜底最小 id */
  private pickTopmostNode(pool: GameNode[]): number | null {
    if (pool.length === 0) return null
    const positionOf = (id: number) => this.graphData.positions[id] || { x: Infinity, y: Infinity }
    const sorted = [...pool].sort((a, b) => {
      const pa = positionOf(a.id!)
      const pb = positionOf(b.id!)
      if (pa.y !== pb.y) return pa.y - pb.y
      if (pa.x !== pb.x) return pa.x - pb.x
      return (a.id || 0) - (b.id || 0)
    })
    return sorted[0].id ?? null
  }

  /** 章节的“入口根节点”：按位置/id 兜底选取（不看链路，仅当链路不可达时使用） */
  private getEntryRootOfChapter(chapterId: number): number | null {
    const chapterNodes = this.nodes.filter((n) => n.id && n.game_chapter_id === chapterId)
    if (chapterNodes.length === 0) return null
    const roots = chapterNodes.filter((n) => this.rootNodeIds.has(n.id!))
    return this.pickTopmostNode(roots.length ? roots : chapterNodes)
  }

  /** 整个小说的第一个节点：第一章入口，兜底全局根节点，再兜底任意节点 */
  getStoryStartNodeId(): number | null {
    // 第一章 = 章节列表首项
    const firstChapter = this.chapters[0]
    if (firstChapter) {
      const picked = this.getEntryRootOfChapter(firstChapter.id)
      if (picked != null) return picked
    }
    // 兜底：全局根节点里最靠上的，再兜底任意节点
    const globalRoots = this.nodes.filter((n) => n.id && this.rootNodeIds.has(n.id))
    return this.pickTopmostNode(globalRoots.length ? globalRoots : this.nodes.filter((n) => n.id))
  }

  /** 构建正向邻接表（源→目标，按结局端口序），用于沿实际链路遍历 */
  private buildChildrenAdjacency(): Record<number, number[]> {
    const connections = this.graphData.connections
    const childrenOf: Record<number, number[]> = {}
    for (const srcIdStr of Object.keys(connections)) {
      const srcId = Number(srcIdStr)
      const conns = connections[srcId]
      const pIdxKeys = Object.keys(conns).map(Number).sort((a, b) => a - b)
      for (const pIdx of pIdxKeys) {
        const targetId = conns[pIdx]
        if (targetId == null) continue
        if (!childrenOf[srcId]) childrenOf[srcId] = []
        if (!childrenOf[srcId].includes(targetId)) childrenOf[srcId].push(targetId)
      }
    }
    return childrenOf
  }

  /**
   * 章节在“实际链路”上的第一个节点：
   * 从整个小说的第一个节点出发、沿跳转连线做 BFS，返回首个属于该章节的可达节点。
   * 这样能跳过那些改跳转后遗留的、没有前序的孤立节点。
   * 链路上完全不可达时，兜底取该章节的入口根节点。
   */
  getFirstNodeIdOfChapter(chapterId: number): number | null {
    const chapterOf = (id: number) => this.nodes.find((n) => n.id === id)?.game_chapter_id
    const start = this.getStoryStartNodeId()
    if (start != null) {
      const childrenOf = this.buildChildrenAdjacency()
      const visited = new Set<number>([start])
      const queue: number[] = [start]
      while (queue.length > 0) {
        const id = queue.shift()!
        if (chapterOf(id) === chapterId) return id
        for (const child of (childrenOf[id] || [])) {
          if (!visited.has(child)) {
            visited.add(child)
            queue.push(child)
          }
        }
      }
    }
    // 链路上不可达：兜底该章节入口根节点
    return this.getEntryRootOfChapter(chapterId)
  }

  /** 第一章第一个节点（打开图谱时的初始定位）= 整个小说的第一个节点 */
  getFirstNodeId(): number | null {
    return this.getStoryStartNodeId()
  }

  /** 章节下的节点数量（用于导航列表展示） */
  chapterNodeCount(chapterId: number): number {
    return this.nodes.filter((n) => n.id && n.game_chapter_id === chapterId).length
  }

  /** 点击章节导航：把视口定位到该章节的第一个节点 */
  focusChapter(chapterId: number) {
    const id = this.getFirstNodeIdOfChapter(chapterId)
    if (id == null) {
      this.$message.info('该章节暂无节点')
      return
    }
    this.activeChapterId = chapterId
    const canvas = this.$refs.graphCanvas as any
    canvas?.focusNode(id)
    // 顺带高亮一下目标节点的详情弹层可选，这里仅定位
  }

  /** 打开图谱后把视口定位到第一章第一个节点 */
  focusFirstNode() {
    this.$nextTick(() => {
      // GraphCanvas 通过 setTimeout(0) 异步渲染，稍作延迟确保节点已创建
      window.setTimeout(() => {
        const id = this.getFirstNodeId()
        if (id == null) return
        // 高亮首章导航项
        const focused = this.nodes.find((n) => n.id === id)
        this.activeChapterId = focused?.game_chapter_id ?? this.chapters[0]?.id ?? null
        const canvas = this.$refs.graphCanvas as any
        canvas?.focusNode(id)
      }, 80)
    })
  }

  // ========== 工具方法 ==========
  textToDialogues(text: string): any[] {
    if (!text || text.trim() === '') return []
    const lines = text.split('\n').filter((line: string) => line.trim() !== '')
    const dialogues: any[] = []
    for (const line of lines) {
      let colonIndex = line.indexOf(':')
      const chineseColonIndex = line.indexOf('：')
      if (colonIndex === -1 && chineseColonIndex === -1) continue
      if (colonIndex === -1) colonIndex = chineseColonIndex
      else if (chineseColonIndex !== -1 && chineseColonIndex < colonIndex) colonIndex = chineseColonIndex
      const roleName = line.substring(0, colonIndex).trim()
      const dialogueText = line.substring(colonIndex + 1).trim()
      const role = this.roles.find((r) => r.name === roleName)
      if (role) {
        dialogues.push({ role_id: role.id, text: dialogueText })
      }
    }
    return dialogues
  }
}
</script>

<style scoped lang="scss">
.node-graph-page {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 128px);
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

// ===== 图谱主体：画布 + 章节导航浮层 =====
.graph-body {
  flex: 1;
  position: relative;
  display: flex;
  overflow: hidden;
}

.chapter-nav {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 20;
  width: 208px;
  max-height: calc(100% - 24px);
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.96);
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: width 0.18s ease;

  &.collapsed {
    width: 40px;
  }

  .chapter-nav-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 10px;
    border-bottom: 1px solid #ebeef5;
    background: #f7f9fc;
    flex-shrink: 0;

    .chapter-nav-title {
      font-size: 13px;
      font-weight: 600;
      color: #303133;
      white-space: nowrap;

      i {
        color: #409eff;
        margin-right: 4px;
      }
    }

    .chapter-nav-toggle {
      cursor: pointer;
      color: #909399;
      font-size: 15px;
      padding: 2px;

      &:hover {
        color: #409eff;
      }
    }
  }

  &.collapsed .chapter-nav-header {
    justify-content: center;
  }

  .chapter-nav-list {
    list-style: none;
    margin: 0;
    padding: 4px 0;
    overflow-y: auto;
    flex: 1;
  }

  .chapter-nav-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 7px 10px;
    cursor: pointer;
    font-size: 13px;
    color: #4b5563;
    line-height: 1.3;
    transition: background 0.12s;

    &:hover {
      background: #f0f7ff;
    }

    &.active {
      background: #ecf5ff;

      .cni-title {
        color: #409eff;
        font-weight: 600;
      }
      .cni-index {
        background: #409eff;
        color: #fff;
      }
    }

    &.empty {
      opacity: 0.55;
    }

    .cni-index {
      flex-shrink: 0;
      width: 18px;
      height: 18px;
      border-radius: 4px;
      background: #eef1f5;
      color: #909399;
      font-size: 11px;
      font-weight: 700;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cni-title {
      flex: 1;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .cni-count {
      flex-shrink: 0;
      min-width: 18px;
      text-align: center;
      padding: 0 5px;
      border-radius: 9px;
      background: #f0f2f5;
      color: #909399;
      font-size: 11px;
    }
  }
}

.context-menu {
  position: fixed;
  z-index: 9999;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  padding: 4px 0;
  min-width: 120px;

  .context-menu-item {
    padding: 8px 16px;
    cursor: pointer;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover {
      background: #f5f7fa;
    }

    &.danger {
      color: #f56c6c;

      &:hover {
        background: #fef0f0;
      }
    }
  }
}

// ===== 节点详情弹层 =====
.node-detail-pop {
  position: fixed;
  z-index: 9998;
  width: 320px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.16);
  overflow: hidden;
  font-size: 13px;

  .ndp-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    border-bottom: 1px solid #ebeef5;

    &.is-ai {
      background: linear-gradient(135deg, #e3f0ff 0%, #cfe4ff 100%);
    }
    &.is-fixed {
      background: linear-gradient(135deg, #f0f2f5 0%, #e2e6ea 100%);
    }

    .ndp-badge {
      flex-shrink: 0;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 700;
      color: #fff;
      background: #78909c;
      letter-spacing: 0.5px;
    }
    &.is-ai .ndp-badge {
      background: #1976d2;
    }

    .ndp-title {
      flex: 1;
      font-weight: 600;
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .ndp-id {
      flex-shrink: 0;
      color: #909399;
      font-size: 12px;
    }
    .ndp-be {
      flex-shrink: 0;
      padding: 2px 6px;
      border-radius: 4px;
      background: #f56c6c;
      color: #fff;
      font-size: 11px;
      font-weight: 700;
    }
  }

  .ndp-body {
    padding: 10px 12px;
    max-height: 240px;
    overflow-y: auto;
  }

  .ndp-row {
    display: flex;
    gap: 8px;
    margin-bottom: 6px;
    line-height: 1.5;

    .ndp-label {
      flex-shrink: 0;
      width: 56px;
      color: #909399;
    }
    .ndp-val {
      flex: 1;
      color: #303133;
    }
  }

  .ndp-section {
    margin: 8px 0 6px;
    font-weight: 600;
    color: #606266;
    border-top: 1px dashed #ebeef5;
    padding-top: 8px;
  }

  .ndp-empty {
    color: #c0c4cc;
    font-style: italic;
  }

  .ndp-jump {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 3px 0;
    line-height: 1.5;

    .ndp-ename {
      flex-shrink: 0;
      max-width: 120px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      font-weight: 500;
      color: #409eff;
    }
    .ndp-arrow {
      color: #c0c4cc;
    }
    .ndp-target {
      color: #303133;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      em {
        color: #909399;
        font-style: normal;
        font-size: 12px;
      }
      &.none {
        color: #c0c4cc;
      }
    }
  }

  .ndp-tip {
    padding: 6px 12px;
    background: #fafafa;
    border-top: 1px solid #ebeef5;
    color: #909399;
    font-size: 11px;
    text-align: center;
  }
}
</style>
